/**
 * DropdownMenu — Radix wrapped, editorial styling.
 * Hairline border, no shadow. Slow fade-in.
 */
import * as React from 'react';
import * as DM from '@radix-ui/react-dropdown-menu';
import { cn } from '../../lib/cn';

export const DropdownMenuRoot = DM.Root;
export const DropdownMenuTrigger = DM.Trigger;
export const DropdownMenuGroup = DM.Group;
export const DropdownMenuLabel = DM.Label;
export const DropdownMenuSeparator: React.FC<DM.DropdownMenuSeparatorProps> = (
  props,
) => (
  <DM.Separator
    {...props}
    className={cn('my-1 h-px bg-ink-200', props.className)}
  />
);

export const DropdownMenuContent: React.FC<DM.DropdownMenuContentProps> = ({
  className,
  sideOffset = 6,
  ...props
}) => (
  <DM.Portal>
    <DM.Content
      sideOffset={sideOffset}
      className={cn(
        'z-50 min-w-[12rem]',
        'bg-bg border border-ink-200 p-1',
        'data-[state=open]:animate-[fade-in_160ms_var(--ease-out-slow)]',
        'focus:outline-none',
        className,
      )}
      {...props}
    />
  </DM.Portal>
);

export interface DropdownMenuItemProps extends DM.DropdownMenuItemProps {
  active?: boolean;
}

export const DropdownMenuItem: React.FC<DropdownMenuItemProps> = ({
  className,
  active,
  ...props
}) => (
  <DM.Item
    className={cn(
      'flex items-center gap-2 px-3 py-2',
      'font-sans text-sm text-ink-900',
      'cursor-pointer select-none',
      'transition-colors duration-100 ease-out-slow',
      'data-[highlighted]:bg-surface-2 data-[highlighted]:text-ink-900',
      'data-[disabled]:opacity-40 data-[disabled]:pointer-events-none',
      'focus:outline-none',
      active && 'pl-2',
      className,
    )}
    {...props}
  />
);

/**
 * Convenience compound — single import surface for components downstream.
 */
export const DropdownMenu = {
  Root: DropdownMenuRoot,
  Trigger: DropdownMenuTrigger,
  Content: DropdownMenuContent,
  Item: DropdownMenuItem,
  Group: DropdownMenuGroup,
  Label: DropdownMenuLabel,
  Separator: DropdownMenuSeparator,
};
